<script setup>
/**
 * Opções do WhatsApp Baileys em seções (Atendimento, Histórico, Mais opções).
 * Usado na criação da caixa e nas configurações dela, para as duas telas ficarem iguais.
 * Não mexe nas configurações recebidas: avisa cada mudança com `update` e quem usa decide.
 */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import BaileysOptionRow from './BaileysOptionRow.vue';
import {
  baileysConfigSections,
  isConfigItemVisible,
} from './baileysConfigSections';

const props = defineProps({
  settings: { type: Object, required: true },
  maxImportDays: { type: Number, default: 30 },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['update', 'change', 'blurMsg', 'blurNumber']);
const { t } = useI18n();

const sections = computed(() => baileysConfigSections(t, props.maxImportDays));
const showMoreOptions = ref(false);

const isOpen = section => !section.collapsible || showMoreOptions.value;
const visibleItems = section =>
  section.items.filter(item => isConfigItemVisible(item, props.settings));

const update = (key, value) => emit('update', { key, value });
</script>

<template>
  <div class="flex flex-col gap-8">
    <section v-for="section in sections" :key="section.id">
      <button
        v-if="section.collapsible"
        type="button"
        class="flex items-center gap-1.5 p-0 text-sm font-semibold text-n-slate-12 hover:text-n-slate-11 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand rounded"
        :aria-expanded="showMoreOptions"
        :aria-controls="`baileys-section-${section.id}`"
        @click="showMoreOptions = !showMoreOptions"
      >
        <span
          class="i-lucide-chevron-right size-4 text-n-slate-11"
          :class="{ 'rotate-90': showMoreOptions }"
          aria-hidden="true"
        />
        {{ section.title }}
        <span class="font-normal text-n-slate-11">
          {{ `(${section.items.length})` }}
        </span>
      </button>
      <header v-else class="flex items-baseline gap-2">
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ section.title }}
        </h3>
        <p v-if="section.subtitle" class="text-sm text-n-slate-11">
          {{ section.subtitle }}
        </p>
      </header>

      <div
        v-if="isOpen(section)"
        :id="`baileys-section-${section.id}`"
        class="mt-2 border-t border-n-weak divide-y divide-n-weak"
      >
        <BaileysOptionRow
          v-for="attr in visibleItems(section)"
          :key="attr.key"
          :attribute="attr"
          :model-value="settings[attr.key]"
          :msg-call-value="settings.msgCall"
          :disabled="disabled"
          @update:model-value="value => update(attr.key, value)"
          @update:msg-call-value="value => update('msgCall', value)"
          @change="emit('change', $event)"
          @blur-msg="emit('blurMsg', $event)"
          @blur-number="emit('blurNumber', $event)"
        />
      </div>
    </section>
  </div>
</template>
